import { z } from "zod";
export const PublicTrustBadgeTypes = [
    "discord_verified",
    "discord_partnered",
    "ambassador",
    "power_user",
    "early_user",
    "top_5",
    "top_15",
    "top_100",
];
export const PublicSurfaceTypes = [
    "trust",
    "directory",
    "profile",
];
export const PublicServerDirectorySortTypes = [
    "members",
    "online",
    "voice",
    "name",
];
export const PublicServerJoinSourceTypes = [
    "vanity",
    "widget",
    "manual",
];
export const PublicServerHistoryMetricTypes = [
    "members",
    "online",
    "voice",
];
export const PublicServerHistoryResolutionTypes = [
    "hour",
    "day",
];
export const PublicTrustBadgeTypeSchema = z.enum(PublicTrustBadgeTypes);
export const PublicSurfaceSchema = z.enum(PublicSurfaceTypes);
export const PublicServerDirectorySortSchema = z.enum(PublicServerDirectorySortTypes);
export const PublicServerJoinSourceSchema = z.enum(PublicServerJoinSourceTypes);
export const PublicServerHistoryMetricSchema = z.enum(PublicServerHistoryMetricTypes);
export const PublicServerHistoryResolutionSchema = z.enum(PublicServerHistoryResolutionTypes);
export const PublicServerJoinAccessSchema = z.discriminatedUnion("kind", [
    z.object({
        kind: z.literal("direct"),
        url: z.url(),
        source: PublicServerJoinSourceSchema,
    }),
    z.object({
        kind: z.literal("paused"),
        url: z.url().nullable(),
        until: z.iso.datetime().nullable(),
        source: PublicServerJoinSourceSchema.nullable(),
    }),
    z.object({
        kind: z.literal("application"),
        url: z.url(),
        source: z.literal("manual"),
    }),
    z.object({
        kind: z.literal("none"),
    }),
]);
export const PublicServerBadgeSchema = z.object({
    type: PublicTrustBadgeTypeSchema,
    label: z.string().min(1),
});
export const PublicServerProfileSchema = z.object({
    guildId: z.string().regex(/^\d{16,22}$/),
    vanitySlug: z.string().regex(/^[a-z0-9_-]{2,100}$/).nullable(),
    name: z.string().min(1),
    description: z.string().nullable(),
    avatarUrl: z.url().nullable(),
    memberCount: z.number().int().nonnegative(),
    approximateOnlineCount: z.number().int().nonnegative().nullable(),
    currentVoiceCount: z.number().int().nonnegative(),
    badges: z.array(PublicServerBadgeSchema),
    join: PublicServerJoinAccessSchema,
});
export const PublicServerDirectoryResponseSchema = z.object({
    items: z.array(PublicServerProfileSchema),
    pagination: z.object({
        page: z.number().int().positive(),
        pageSize: z.number().int().positive(),
        totalItems: z.number().int().nonnegative(),
        totalPages: z.number().int().nonnegative(),
    }),
    bounds: z.object({
        membersMax: z.number().int().nonnegative(),
        onlineMax: z.number().int().nonnegative(),
        voiceMax: z.number().int().nonnegative(),
    }),
});
export const PublicTrustServerSchema = PublicServerProfileSchema.omit({
    join: true,
});
export const PublicTrustTestimonialSchema = z.object({
    id: z.string().min(1),
    guildId: z.string().regex(/^\d{16,22}$/).nullable(),
    authorName: z.string().min(1),
    authorRole: z.string().nullable(),
    avatarUrl: z.url().nullable(),
    quote: z.string().min(1),
    badges: z.array(PublicServerBadgeSchema),
});
export const PublicTrustResponseSchema = z.object({
    stats: z.object({
        serverCount: z.number().int().nonnegative(),
        totalMembers: z.number().int().nonnegative(),
        totalOnline: z.number().int().nonnegative(),
        discordVerifiedCount: z.number().int().nonnegative(),
        discordPartneredCount: z.number().int().nonnegative(),
    }),
    servers: z.array(PublicTrustServerSchema),
    testimonials: z.array(PublicTrustTestimonialSchema),
});
export const PublicServerHistoryHttpQuerySchema = z.object({
    metric: PublicServerHistoryMetricSchema.optional(),
    resolution: PublicServerHistoryResolutionSchema.optional(),
    from: z.iso.datetime().optional(),
    to: z.iso.datetime().optional(),
}).strict();
export const PublicServerHistoryResponseSchema = z.object({
    guildId: z.string().regex(/^\d{16,22}$/),
    metric: PublicServerHistoryMetricSchema,
    resolution: PublicServerHistoryResolutionSchema,
    period: z.object({
        from: z.iso.datetime(),
        to: z.iso.datetime(),
    }),
    points: z.array(z.object({
        at: z.iso.datetime(),
        value: z.number().nonnegative(),
    })),
});
const OptionalQueryIntegerSchema = z.preprocess((value) => value === undefined || value === ""
    ? undefined
    : value, z.coerce.number().int().nonnegative().optional());
export const PublicServerDirectoryHttpQuerySchema = z.object({
    q: z.string().trim().max(100).optional(),
    membersMin: OptionalQueryIntegerSchema,
    membersMax: OptionalQueryIntegerSchema,
    onlineMin: OptionalQueryIntegerSchema,
    onlineMax: OptionalQueryIntegerSchema,
    voiceMin: OptionalQueryIntegerSchema,
    voiceMax: OptionalQueryIntegerSchema,
    sort: PublicServerDirectorySortSchema.optional(),
    page: z.preprocess((value) => value === undefined || value === ""
        ? undefined
        : value, z.coerce.number().int().min(1).max(10_000).optional()),
}).strict().superRefine((query, context) => {
    for (const [minimumKey, maximumKey] of [
        ["membersMin", "membersMax"],
        ["onlineMin", "onlineMax"],
        ["voiceMin", "voiceMax"],
    ]) {
        const minimum = query[minimumKey];
        const maximum = query[maximumKey];
        if (minimum !== undefined && maximum !== undefined && minimum > maximum) {
            context.addIssue({
                code: "custom",
                message: `${minimumKey} must be less than or equal to ${maximumKey}`,
                path: [minimumKey],
            });
        }
    }
});
const PublicServerIdPattern = /^\d{16,22}$/;
const PublicServerVanityPattern = /^[a-z0-9_-]{2,100}$/;
export function normalizePublicServerVanitySlug(value) {
    if (typeof value !== "string")
        return null;
    const normalized = value.trim().toLowerCase();
    return PublicServerVanityPattern.test(normalized) && !/^\d+$/.test(normalized)
        ? normalized
        : null;
}
export function normalizePublicServerReference(value) {
    const normalized = value.trim().toLowerCase();
    if (PublicServerIdPattern.test(normalized))
        return normalized;
    return normalizePublicServerVanitySlug(normalized);
}
export function getPublicServerCanonicalReference(server) {
    return server.vanitySlug ?? server.guildId;
}
export function createPublicServerCanonicalPath(pathname, server) {
    const segments = pathname.split("/");
    segments[segments.length - 1] = encodeURIComponent(getPublicServerCanonicalReference(server));
    return segments.join("/");
}
export function resolvePublicServerCanonicalRoute(pathname, requestedReference, server) {
    const canonicalReference = getPublicServerCanonicalReference(server);
    const canonicalPath = createPublicServerCanonicalPath(pathname, server);
    if (requestedReference === canonicalReference)
        return {
            kind: "canonical",
            canonicalPath,
        };
    return {
        kind: "redirect",
        location: canonicalPath,
        statusCode: 307,
    };
}
export function toPublicServerDirectoryQuery(query) {
    return {
        search: query.q,
        membersMin: query.membersMin,
        membersMax: query.membersMax,
        onlineMin: query.onlineMin,
        onlineMax: query.onlineMax,
        voiceMin: query.voiceMin,
        voiceMax: query.voiceMax,
        sort: query.sort,
        page: query.page,
    };
}
//# sourceMappingURL=contracts.js.map